import React, { useContext, useState } from 'react';

import { CornerDownRight } from 'lucide-react';
import { type Dispatch, compose } from 'redux';

import { reloadCompany } from 'common/actions/company';
import AJAX from 'common/AJAX';
import Pill, { DefaultPillStyles } from 'common/common/Pill';
import TextToggle from 'common/common/TextToggle';
import SyncIntegrationNames from 'common/constants/syncIntegrationNames';
import { BoardsContext } from 'common/containers/BoardsContainer';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { ShowToastContext, ToastTypes } from 'common/containers/ToastContainer';
import connect from 'common/core/connect';
import Helmet from 'common/helmets/Helmet';
import withAccessControl from 'common/routing/withAccessControl';
import SingleSelectWithSearch from 'common/ui/SingleSelectWithSearch';
import SwitchV2 from 'common/ui/SwitchV2';
import { H1, P } from 'common/ui/Text';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';
import { RoutePermissions, testEveryPermission } from 'common/util/permissions';

import AdminAutopilotIntegrationItem, {
  type IntegrationItem as Integration,
} from './AdminAutopilotIntegrationItem';

import GongLogo from 'img/gong-logo.png';
import HelpscoutLogo from 'img/helpscout-logo-small.png';
import IntercomLogo from 'img/intercom-logo-small.png';
import ZendeskLogo from 'img/zendesk-logo-small.png';

import type { Company } from 'common/api/endpoints/companies';

import 'css/components/subdomain/admin/_AdminFeedbackDiscoverySettings.scss';

type OwnProps = Record<string, never>;

type ConnectProps = {
  reloadCompany: () => Promise<void>;
};

type Props = OwnProps & ConnectProps;

const AdminFeedbackDiscoverySettings = ({ reloadCompany }: Props) => {
  const boards = useContext(BoardsContext);
  const company = useContext<Company>(CompanyContext);
  const showToast = useContext(ShowToastContext);

  const [enabled, setEnabled] = useState(!!company.queueAutomation?.enabled);
  const [boardID, setBoardID] = useState<string | null>(company.queueAutomation?.boardID ?? null);
  const [loading, setLoading] = useState<boolean>(false);

  const Integrations: Partial<Record<Exclude<SyncIntegrationNames, 'api'>, Integration>> = {
    intercom: {
      credits: 1,
      label: 'Intercom',
      logo: IntercomLogo,
      name: SyncIntegrationNames.intercom,
      type: 'conversation',
    },
    helpscout: {
      credits: 1,
      label: 'Help Scout',
      logo: HelpscoutLogo,
      name: SyncIntegrationNames.helpscout,
      type: 'conversation',
    },
    zendesk: {
      credits: 1,
      label: 'Zendesk',
      logo: ZendeskLogo,
      name: SyncIntegrationNames.zendesk,
      type: 'conversation',
    },
  };

  if (company.featureAllowlist?.includes('queue')) {
    Integrations.gong = {
      credits: 5,
      label: 'Gong',
      logo: GongLogo,
      name: SyncIntegrationNames.gong,
      type: 'call',
    };
  }

  // handle boards loading state
  if ('loading' in boards) {
    return null;
  }

  const options = boards.map((board) => ({
    label: board.name,
    value: board._id,
  }));

  const updatePostDeduplication = async (enabled: boolean) => {
    const response = await AJAX.post('/api/queue/togglePostDeduplication', {
      enabled,
    });

    const { error } = parseAPIResponse(response, {
      isSuccessful: isDefaultSuccessResponse,
    });

    if (error) {
      showToast(error.message, ToastTypes.error);
      return;
    }

    await reloadCompany();
  };

  const updateQueueAutomation = async (enabled: boolean, boardID: string | null) => {
    setBoardID(boardID);
    setEnabled(enabled);

    if (!boardID) {
      return;
    }

    setLoading(true);

    const response = await AJAX.post('/api/queue/toggleAutomation', {
      boardID,
      enabled,
    });

    setLoading(false);

    const { error } = parseAPIResponse(response, {
      isSuccessful: isDefaultSuccessResponse,
    });

    if (error) {
      showToast(error.message, ToastTypes.error);
      return;
    }

    await reloadCompany();
  };

  return (
    <>
      <Helmet title={`Feedback Discovery | Autopilot Settings | Canny`} />
      <div className="adminQueueSettings">
        <div className="feedbackDiscoveryModeSection">
          <div className="queueToggleSection">
            <div className="queueDetails">
              <H1 variant="headingMd">Feedback Suggestions</H1>
              <P className="queueBody">
                Choose how you would like feedback suggestions to be handled. You can either
                manually review the actions or automate them, with the ability to reverse them as
                needed.
              </P>
            </div>
            <TextToggle
              className="enableToggle"
              disabled={loading}
              onChange={(value) => updateQueueAutomation(value === 'automatic', boardID)}
              options={[
                { value: 'manual', label: 'Manual' },
                { value: 'automatic', label: 'Automated' },
              ]}
              selected={enabled ? 'automatic' : 'manual'}
            />
          </div>
          {enabled ? (
            <div className="boardSelectSection">
              <P className="boardSelectLabel" variant="bodyMd">
                <CornerDownRight height="16" width="16" />
                Select a destination for generated feedback
              </P>
              <div>
                <SingleSelectWithSearch
                  disabled={loading}
                  onChange={(option) => updateQueueAutomation(enabled, option?.value ?? null)}
                  options={options}
                  placeholder="Board"
                  value={options.find((option) => option.value === boardID)}
                />
              </div>
            </div>
          ) : null}
        </div>
        <div className="queueToggleSection">
          <div className="queueDetails">
            <div className="queueSectionTitle">
              <H1 variant="headingMd">Canny Portal</H1>
              <Pill pillStyle={DefaultPillStyles.canny} className="pill">
                1 credit / post
              </Pill>
            </div>
            <P className="queueBody">
              When new posts are created in the Canny Portal, allow Autopilot to review existing
              posts to find&nbsp;duplicates.
            </P>
          </div>
          <SwitchV2
            size="medium"
            checked={!!company.deduplication?.enabled}
            onChange={updatePostDeduplication}
          />
        </div>
        <div className="queueToggleSection">
          <div className="queueDetails">
            <H1 variant="headingMd">Sources</H1>
            <P className="queueBody">
              Select which sources you would like Autopilot to extract feedback from. Detected
              feedback will also be compared to existing posts to find&nbsp;duplicates.
            </P>
            <div className="integrationsList">
              {Object.values(Integrations).map((integration) => (
                <AdminAutopilotIntegrationItem {...integration} key={integration.name} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default compose(
  connect(null, (dispatch: Dispatch<any>) => ({
    reloadCompany: () => {
      dispatch(reloadCompany());
    },
  })),
  withAccessControl<Props>(
    testEveryPermission(RoutePermissions.adminSettings.inbox),
    '/admin/settings'
  )
)(AdminFeedbackDiscoverySettings) as unknown as React.FC<OwnProps>;
